// core
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// ui primitives (replaced @mui/material — see Components/UI)
import {
    Button,
    Card,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from "Components/UI";

// assets
import whiteLogo from "Assets/logo-rgb-white.svg";

import { faRightToBracket, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// services
import { useAuthenticate } from "Services/Authentication";
import {
    useLoginMagicLink as loginMagicLink,
    useLoginGoogle as loginGoogle,
} from "Services/LoginAPI";

// components
import LoginLogo from "./LoginLogo";
import SocialMedialLogins from "./SocialMedialLogins";

// styles
import "react-toastify/dist/ReactToastify.css";
import styles from "./LoginForm.module.css";
import OrSeparator from "./OrSeparator";

let isUnixiExtensionAllowed = false;
let unixiExtensionEnv = null;
const serverName = window.location.host;

const unixiExtensionExistsWithinThisPageAndReadytoGo = JSON.parse(
    localStorage.getItem(`${serverName}-managementPortalWithUnixi`)
);

let isUnixiExtensionActive = !!unixiExtensionExistsWithinThisPageAndReadytoGo;

isUnixiExtensionAllowed =
    !!unixiExtensionExistsWithinThisPageAndReadytoGo?.allowed;
unixiExtensionEnv = unixiExtensionExistsWithinThisPageAndReadytoGo?.env;

let managementPortalWithUnixi = {
    allowed: isUnixiExtensionAllowed,
    env: unixiExtensionEnv,
};

// main component
export default function MagicLinkForm(props) {
    // injections
    const navigate = useNavigate();

    // own state
    const [username, setUsername] = useState("");
    const [loading, setLoading] = useState(false);
    const [extensionActive] = useState(isUnixiExtensionActive);
    const [magicLinkSent, setMagicLinkSent] = useState(false);

    const isUnixiLogin = extensionActive && managementPortalWithUnixi.allowed;

    // private methods
    function CompleteLogin(response) {
        useAuthenticate(response.user, response.tenant);
        const LandingPage = response.tenant.landing_page;
        props?.animateGlobe?.animation();

        completeRedirect(LandingPage);
    }

    function completeRedirect(LandingPage) {
        const redirectPath = localStorage.getItem("redirectPath") || "/";
        console.log(redirectPath);
        localStorage.removeItem("redirectPath"); // Clear the saved path
        if (redirectPath === "/") {
            window.location.href = LandingPage
                ? `/unixi-management-portal/app/${LandingPage}`
                : "/unixi-management-portal/app/dashboard";
        } else if (redirectPath.startsWith("/unixi-management-portal")) {
            window.location.href = `${redirectPath}`;
        } else {
            window.location.href = `/unixi-management-portal${redirectPath}`;
        }
    }

    function CompleteLoginGoogle(credentialResponse) {
        loginGoogle(credentialResponse)
            .then((response) => {
                if (response && response.status) {
                    if (response.page === "login-mfa") {
                        navigate("login-mfa");
                    } else {
                        CompleteLogin(response);
                    }
                } else if (response) {
                    toast.error(response.info);
                } else {
                    toast.error("Communication error occurred.");
                }
            })
            .catch((error) => {
                toast.error(error.toString());
                console.log(error);
                setLoading(false);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleMagicLinkSubmit(e) {
        setLoading(true);
        e.preventDefault();

        if (!isUnixiLogin && !username) {
            toast.error("Email address is required");
            setLoading(false);
            return;
        }

        loginMagicLink(username)
            .then((response) => {
                if (response && response.status) {
                    toast.success(response.info);
                    setMagicLinkSent(true);
                } else if (response) {
                    toast.error(response?.info);
                }
            })
            .catch((error) => {
                toast.error(error.toString());
                console.log(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function handleMicrosoftLogin() {
        const tenantId = "organizations";
        const clientId = "a3330774-170c-41b9-b3b1-a33a00c2146c";
        const stateObj = { frontend_origin: window.location.origin };
        const state = encodeURIComponent(JSON.stringify(stateObj));

        // Generate a random nonce for security
        const nonce =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);

        // Use your registered redirect URI exactly as is:
        const redirectUri = `${window.location.protocol}//${serverName}/api/authentication/azure_callback`;
        const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=id_token&redirect_uri=${encodeURIComponent(
            redirectUri
        )}&scope=openid%20email%20profile&state=${state}&nonce=${nonce}&response_mode=form_post`;

        window.open(authUrl, "mslogin", "width=600,height=700");

        // Listen for the message from the popup
        const handleMicrosoftMessage = async (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data && event.data.success === "true") {
                let userObj = event.data.user;
                let tenantObj = event.data.tenant;
                const response = {
                    status: true,
                    user: userObj,
                    tenant: tenantObj,
                    page: tenantObj.landing_page,
                };
                cleanup();
                CompleteLogin(response);
            } else if (event.data && event.data.success === "false") {
                toast.error(
                    `ERROR: ${event.data.info || "Microsoft login failed"}`
                );
                cleanup();
            }
        };

        window.addEventListener("message", handleMicrosoftMessage);

        const intervalId = setInterval(() => {
            if (localStorage.getItem("tenant")) {
                let response = {
                    status: true,
                    tenant: JSON.parse(localStorage.getItem("tenant")),
                    page: JSON.parse(localStorage.getItem("tenant"))
                        .landing_page,
                };
                cleanup();
                CompleteLogin(response);
            }
        }, 1000);

        const cleanup = () => {
            window.removeEventListener("message", handleMicrosoftMessage);
            clearInterval(intervalId);
        };
    }

    // render
    return (
        <Card flat>
            <Stack fullWidth gap={3}>
                <Stack align="center" direction="row" justify="center">
                    <LoginLogo />
                </Stack>

                <Stack align="center" direction="row" justify="center">
                    <Typography variant="h6">Please Sign In</Typography>
                </Stack>

                {magicLinkSent ? (
                    <Typography align="center" variant="body1">
                        Magic link sent to your email address.
                    </Typography>
                ) : (
                    <form onSubmit={handleMagicLinkSubmit}>
                        {!isUnixiLogin ? (
                            <Stack gap={3}>
                                <TextField
                                    error={!username}
                                    helperText={
                                        !username && "Email address is required"
                                    }
                                    fullWidth
                                    disabled={loading}
                                    value={username}
                                    onChange={(e) =>
                                        setUsername(e.target.value)
                                    }
                                    label={
                                        <Stack
                                            align="center"
                                            direction="row"
                                            gap={1}
                                        >
                                            <FontAwesomeIcon icon={faUser} />
                                            <Typography variant="body1">
                                                Email Address
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </Stack>
                        ) : null}

                        <div className={styles.submitBlock}>
                            <Button
                                type="submit"
                                fullWidth
                                disabled={loading}
                                endIcon={
                                    loading ? (
                                        <CircularProgress size={18} />
                                    ) : isUnixiLogin ? (
                                        <img
                                            src={whiteLogo}
                                            className={styles.logoButton}
                                            alt="Unixi Logo"
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faRightToBracket}
                                        />
                                    )
                                }
                            >
                                {isUnixiLogin ? "Login with" : "Sign In"}
                            </Button>
                        </div>

                        <Stack gap={2} mt={2}>
                            <OrSeparator />

                            <SocialMedialLogins
                                loading={loading}
                                setLoading={setLoading}
                                onGoogleSuccess={CompleteLoginGoogle}
                                onMicrosoftClick={handleMicrosoftLogin}
                            />
                        </Stack>
                    </form>
                )}
            </Stack>
        </Card>
    );
}
